import { cloneElement, isValidElement, useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent, type ReactElement, type Ref } from "react";
import { Tooltip, useMergeRefs, type TooltipProps } from "@chakra-ui/react";
import { colors } from "../constants/GlobalStyles";
import { isTouchFirstDevice } from "../backend/utils/detect-device";

/** The look of Jungle's hover tooltip: the small tag under the tools beneath a question in a review
 * (`CardActionButtonsAndDropdownMenuComponent` draws that one itself with these values). */
export const APP_TOOLTIP_LOOK = {
  bg: colors.surface.border, color: colors.text.body, fontSize: "14px", fontWeight: 400, lineHeight: "20px",
  padding: "4px 10px 6px", borderRadius: "8px", boxShadow: "none",
} as const;

/** Under its target, 8px away, at once, no arrow; a long label wraps. Callers pass only what differs. */
export const APP_TOOLTIP_STYLE = {
  ...APP_TOOLTIP_LOOK, placement: "bottom", gutter: 8, openDelay: 0, hasArrow: false, maxW: "320px",
} as const satisfies Partial<TooltipProps>;

// Surfaces that redefine the surface colors for what lies on them (src/theme/jungleThemeColors.css).
const FLOATING_SURFACE = ".chakra-modal__content, .chakra-menu__menu-list, .chakra-popover__content, .jungle-floating, .jungle-floating-control, .jungle-raised-stage";

const isKeyboardFocus = (element: EventTarget) => {
  try { return element instanceof Element && element.matches(":focus-visible"); } catch { return true; }
};

/**
 * The learner app's hover tooltip; use it for every hover label instead of Chakra's `Tooltip` or a native `title`.
 * It also opens on keyboard focus, and never on a touch-first device (a phone, a tablet without a mouse; the
 * mobile app runs on those), where a tap would leave it stuck open. The target keeps its own `aria-label`.
 */
export function AppTooltip({ isDisabled, children, ...props }: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Chakra opens on any focus. Focus a click leaves behind, or a closing menu or dialog hands back, is not
  // the learner asking what the control is, so only keyboard focus (`:focus-visible`) or the pointer opens it.
  const isFocusWithoutKeyboard = useRef(false);
  // Drawn at the end of the page, a tooltip over a dialog would take the page's colors: its tag would vanish
  // into a dark dialog. It takes the floating surface's colors instead.
  const [isOnFloatingSurface, setIsOnFloatingSurface] = useState(false);
  const notice = (target: EventTarget) => setIsOnFloatingSurface(target instanceof Element && !!target.closest(FLOATING_SURFACE));
  const childProps = isValidElement<Record<string, unknown>>(children) ? children.props : null;
  const trigger = childProps ? cloneElement(children as ReactElement, {
    onFocus: (event: FocusEvent) => { (childProps.onFocus as ((event: FocusEvent) => void) | undefined)?.(event); isFocusWithoutKeyboard.current = !isKeyboardFocus(event.target); notice(event.target); },
    onPointerEnter: (event: PointerEvent) => { (childProps.onPointerEnter as ((event: PointerEvent) => void) | undefined)?.(event); isFocusWithoutKeyboard.current = false; notice(event.currentTarget); },
  }) : children;
  const openState = props.isOpen !== undefined ? {} : {
    isOpen,
    onOpen: () => { if (isFocusWithoutKeyboard.current) return; setIsOpen(true); props.onOpen?.(); },
    onClose: () => { setIsOpen(false); props.onClose?.(); },
  };
  const className = [props.className, isOnFloatingSurface && "jungle-tooltip-on-floating"].filter(Boolean).join(" ") || undefined;
  return <Tooltip {...APP_TOOLTIP_STYLE} {...props} {...openState} className={className} isDisabled={isDisabled || isTouchFirstDevice}>{trigger}</Tooltip>;
}

/** `AppTooltip` for text cut off with an ellipsis (or clamped lines): it shows the whole text, only while it is cut. */
export function AppTooltipWhenCutOff({ children, isDisabled, ...props }: TooltipProps & { children: ReactElement }) {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isCutOff, setIsCutOff] = useState(false);
  useLayoutEffect(() => {
    if (!element) return;
    // Only clamped lines are measured by height: a single line's glyphs can overhang its line box by a pixel.
    const isLineClamped = () => !["", "none"].includes(getComputedStyle(element).getPropertyValue("-webkit-line-clamp"));
    const measure = () => setIsCutOff(element.scrollWidth > element.clientWidth + 1 ||
      (isLineClamped() && element.scrollHeight > element.clientHeight + 1));
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, props.label]);
  const ref = useMergeRefs(setElement, (children as ReactElement & { ref?: Ref<HTMLElement> }).ref);
  return <AppTooltip {...props} isDisabled={isDisabled || !isCutOff}>{cloneElement(children, { ref })}</AppTooltip>;
}
